import { Link, useLocation } from "@remix-run/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import LogoIcon from "~/components/icons/LogoIcon";
import MenuIndicator from "~/components/icons/MenuIndicator";
import { cn, getMenuHref } from "~/lib/utils";
import { useAppStore } from "~/stores/app";

interface MobileMenuProps {
  bookingDisabled?: boolean;
}

export const MobileMenu = ({ bookingDisabled = false }: MobileMenuProps) => {
  const { mobileMenuOpened, toggleMobileMenu } = useAppStore();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (mobileMenuOpened) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpened]);
  const navItems = [
    // {
    //   name: t("Il tuo Account"),
    //   href: getMenuHref(i18n.language, "/user/account"),
    // },
    { name: t("Destinazione"), href: getMenuHref(i18n.language, "/about-us") },
    { name: t("Mondi"), href: getMenuHref(i18n.language, "/worlds") },
    // `/servizi` is deliberately absent: the page is published but must stay
    // reachable by direct URL only, so it can be shown to the client without
    // being exposed to visitors. Same reason it is out of the sitemap and
    // carries `noindex, nofollow`.
    { name: t("Sapori"), href: getMenuHref(i18n.language, "/food") },
    { name: t("Massaggi"), href: getMenuHref(i18n.language, "/massages") },
    { name: t("Eventi"), href: getMenuHref(i18n.language, "/events") },
    { name: t("Press"), href: getMenuHref(i18n.language, "/press") },
    // {
    //   name: t("Esperienze"),
    //   href: getMenuHref(i18n.language, "/experiences"),
    // },
  ];
  return (
    <div
      className={cn(
        "w-full flex flex-column fixed left-0 top-0 h-full z-20 transition-all",
        {
          "pointer-events-none": !mobileMenuOpened,
          "pointer-events-all": mobileMenuOpened,
        }
      )}
    >
      <div
        role="presentation"
        className={cn(
          "w-full h-full top-0 left-0 fixed bg-black z-10 transition-all duration-300",
          {
            "opacity-0": !mobileMenuOpened,
            "opacity-95": mobileMenuOpened,
            "pointer-events-none": !mobileMenuOpened,
            "pointer-events-all": mobileMenuOpened,
          }
        )}
        onClick={toggleMobileMenu}
      />
      <div
        className={cn(
          "w-full flex flex-col top-0 h-full items-center z-20 p-3.5 lg:p-6",
          {
            "opacity-0": !mobileMenuOpened,
            "opacity-80": mobileMenuOpened,
            "pointer-events-none": !mobileMenuOpened,
            "pointer-events-all": mobileMenuOpened,
          }
        )}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            toggleMobileMenu();
          }
        }}
      >
        <Link to={getMenuHref(i18n.language, "/")} onClick={toggleMobileMenu}>
          <LogoIcon className="w-24 lg:w-40 h-12 fill-white" />
        </Link>
        <div className="h-20" />
        <ul
          className={cn(
            "items-center justify-center space-y-8 flex flex-col transition-all duration-300",
            {
              "opacity-0 translate-y-full": !mobileMenuOpened,
              "opacity-100 translate-y-0": mobileMenuOpened,
            }
          )}
        >
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <li key={item.name} className="group flex flex-col relative">
                <Link
                  onClick={toggleMobileMenu}
                  to={item.href}
                  className="relative text-white font-medium text-2xl"
                >
                  {item.name}
                </Link>
                <div className="absolute -bottom-2 left-0 w-full flex justify-center overflow-hidden">
                  <MenuIndicator
                    className={cn(
                      "fill-white transition-all duration-300",
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    )}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
